import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import { useI18nLocale } from '@/hooks/useTranslation';
import { Group, Circle, Rect, Text } from 'react-konva';
import type { RefObject } from 'react';
import Konva from 'konva';
import type { CommentNode } from '../../domains/comments/models/Comment';
import { useCommentOverlayStore } from '../../stores/commentOverlayStore';
import { hexToRgba, mixHexColors, normaliseHex } from '../../lib/color/hex';
import { resolveCommentAuthorVisual } from '../../lib/comments/commentAuthor';
import { renderTokensToDisplay } from '../../lib/comments/mentionTokens';
import { formatRelativeTime } from '../../lib/datetime/relativeTime';
import type { KonvaEventObject } from 'konva/lib/Node';

const FALLBACK_COLOR = '#2563EB';
const FALLBACK_ACTIVE_COLOR = '#1D4ED8';
const RESOLVED_TINT_HEX = '#94A3B8';

interface CommentPinsLayerProps {
  comments: CommentNode[];
  stageRef: RefObject<Konva.Stage>;
  activeCommentId: string | null;
}

export const CommentPinsLayer: React.FC<CommentPinsLayerProps> = memo(({
  comments,
  stageRef,
  activeCommentId,
}) => {
  const timeLocale = useI18nLocale();
  const selectComment = useCommentOverlayStore((state) => state.selectComment);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const hoveredNodeRef = useRef<Konva.Group | null>(null);
  const palette = getThemePalette();

  useEffect(() => {
    if (activeCommentId) {
      setHoveredCommentId(null);
      if (hoveredNodeRef.current) {
        animateNodeScale(hoveredNodeRef.current, 1);
        hoveredNodeRef.current = null;
      }
    }
  }, [activeCommentId]);

  const positionedComments = useMemo(
    () => comments.filter((comment) => Boolean(comment.locOnDiagram)),
    [comments],
  );

  const orderedComments = useMemo(() => {
    if (!hoveredCommentId) {
      return positionedComments;
    }
    const hoveredComment = positionedComments.find((comment) => comment.id === hoveredCommentId);
    if (!hoveredComment) {
      return positionedComments;
    }
    return [...positionedComments.filter((comment) => comment.id !== hoveredCommentId), hoveredComment];
  }, [hoveredCommentId, positionedComments]);

  const handleSelect = (commentId: string) => {
    selectComment(commentId);
  };

  const handleMouseEnter = (commentId: string, event: KonvaEventObject<MouseEvent>) => {
    if (activeCommentId) {
      return;
    }
    const node = event.currentTarget as Konva.Group;
    hoveredNodeRef.current = node;
    animateNodeScale(node, 1.08);
    setHoveredCommentId(commentId);
    const container = stageRef.current?.container();
    if (!container) return;
    container.setAttribute('data-prev-cursor', container.style.cursor ?? '');
    container.style.cursor = 'pointer';
  };

  const handleMouseLeave = (event: KonvaEventObject<MouseEvent>) => {
    const node = event.currentTarget as Konva.Group;
    animateNodeScale(node, 1);
    hoveredNodeRef.current = null;
    setHoveredCommentId(null);
    const container = stageRef.current?.container();
    if (!container) return;
    const previous = container.getAttribute('data-prev-cursor');
    if (previous && previous.length > 0) {
      container.style.cursor = previous;
    } else {
      container.style.removeProperty('cursor');
    }
  };

  return (
    <>
      {orderedComments.map((comment) => {
          const location = comment.locOnDiagram!;
          const replyCount = comment.replies?.length ?? 0;
          const isActive = comment.id === activeCommentId;
          const isResolved = comment.state === 'resolved';
          const isHovered = hoveredCommentId === comment.id && !activeCommentId;
          const showPreview = isHovered;

          const commentColorValue = comment.metadata?.color;
          const commentHex = typeof commentColorValue === 'string' ? normaliseHex(commentColorValue) : null;
          const fallbackColor = isActive ? FALLBACK_ACTIVE_COLOR : FALLBACK_COLOR;
          const unresolvedBase = commentHex
            ? isActive
              ? mixHexColors(commentHex, '#FFFFFF', 0.12)
              : commentHex
            : fallbackColor;
          const displayColor = isResolved
            ? mixHexColors(unresolvedBase, RESOLVED_TINT_HEX, 0.35)
            : unresolvedBase;
          const shadowColor = hexToRgba(unresolvedBase, isResolved ? 0.25 : 0.35);
          const badgeColor = displayColor;
          const radius = 11;

          const author = resolveCommentAuthorVisual(
            comment.authorId ?? null,
            comment.author ?? null,
            comment.metadata?.color ?? null,
          );
          const relativeTimestamp = formatRelativeTime(comment.createdAt, timeLocale);
          const previewText = buildRootPreview(comment.body);
          const truncatedPreview = truncatePreview(previewText);
          const totalReplies = countReplies(comment.replies);
          const hasReplies = totalReplies > 0;
          const replyLabel = hasReplies ? `${totalReplies} ${totalReplies === 1 ? 'reply' : 'replies'}` : '';

          const tooltipWidth = 248;
          const paddingX = 12;
          const paddingY = 14;
          const avatarSize = 28;
          const avatarRadius = avatarSize / 2;
          const nameLineHeight = 16;
          const previewLines = estimateLineCount(truncatedPreview);
          const previewLineHeight = 16;
          const replyLineHeight = 14;
          const spacerSmall = 4;
          const spacerMedium = 8;
          const headerBottomY = paddingY + avatarSize;
          const dividerY = headerBottomY + spacerSmall;
          const bodyY = dividerY + spacerMedium;
          const bodyHeight = previewLines * previewLineHeight;
          const repliesY = bodyY + bodyHeight + (hasReplies ? spacerMedium : 0);
          const tooltipHeight = repliesY + (hasReplies ? replyLineHeight : 0) + paddingY;
          const baseOffsetX = radius + 12;
          const baseOffsetY = -(tooltipHeight + radius + 10);
          const { offsetX: tooltipOffsetX, offsetY: tooltipOffsetY } = computeTooltipOffsets({
            stage: stageRef.current ?? null,
            location,
            width: tooltipWidth,
            height: tooltipHeight,
            baseOffsetX,
            baseOffsetY,
            margin: 16,
          });

          return (
            <Group
              key={comment.id}
              x={location.x}
              y={location.y}
              name="comment-pin"
              listening
              onClick={(evt) => {
                evt.cancelBubble = true;
                handleSelect(comment.id);
              }}
              onTap={(evt) => {
                evt.cancelBubble = true;
                handleSelect(comment.id);
              }}
              onMouseEnter={(evt) => handleMouseEnter(comment.id, evt)}
              onMouseLeave={(evt) => handleMouseLeave(evt)}
            >
              <Circle
                radius={radius}
                fill={displayColor}
                opacity={isResolved ? 0.75 : 0.92}
                shadowBlur={6}
                shadowColor={shadowColor}
              />
              <Rect x={-1} y={-6} width={2} height={12} cornerRadius={1} fill="#ffffff" />
              <Rect x={-6} y={-1} width={12} height={2} cornerRadius={1} fill="#ffffff" />
              {replyCount > 0 && (
                <Group y={14} listening={false}>
                  <Rect
                    x={-10}
                    y={-8}
                    width={20}
                    height={16}
                    cornerRadius={8}
                    fill={badgeColor}
                    opacity={isResolved ? 0.7 : 0.85}
                  />
                  <Text
                    text={`${replyCount + 1}`}
                    fontSize={11}
                    fill="#ffffff"
                    align="center"
                    width={20}
                    offsetX={10}
                    offsetY={6}
                  />
                </Group>
              )}
              {showPreview ? (
                <Group
                  listening={false}
                  x={tooltipOffsetX}
                  y={tooltipOffsetY}
                >
                  <Rect
                    width={tooltipWidth}
                    height={tooltipHeight}
                    cornerRadius={8}
                    fill={palette.surface}
                    shadowBlur={18}
                    shadowOffsetY={12}
                    shadowColor={palette.shadow}
                    stroke={palette.border}
                    strokeWidth={1}
                  />
                  <Circle
                    x={paddingX + avatarRadius}
                    y={paddingY + avatarRadius}
                    radius={avatarRadius}
                    fill={author.color ?? palette.primary}
                  />
                  <Text
                    text={author.fallbackText}
                    x={paddingX}
                    y={paddingY + avatarRadius - 5}
                    width={avatarSize}
                    align="center"
                    fontSize={12}
                    fontStyle="bold"
                    fill="#FFFFFF"
                  />
                  <Text
                    text={author.displayName}
                    x={paddingX + avatarSize + spacerMedium}
                    y={paddingY + (avatarSize - nameLineHeight) / 2}
                    width={tooltipWidth - paddingX * 2 - avatarSize - spacerMedium}
                    fontSize={13}
                    fontStyle="bold"
                    fill={palette.text}
                  />
                  <Text
                    text={relativeTimestamp}
                    x={paddingX + avatarSize + spacerMedium}
                    y={paddingY + (avatarSize - nameLineHeight) / 2 + nameLineHeight + 2}
                    fontSize={11}
                    fill={palette.muted}
                  />
                  <Text
                    text={truncatedPreview}
                    x={paddingX}
                    y={bodyY}
                    width={tooltipWidth - paddingX * 2}
                    fontSize={12}
                    lineHeight={1.35}
                    fill={palette.text}
                  />
                  {hasReplies ? (
                    <Text
                      text={replyLabel}
                      x={paddingX}
                      y={repliesY}
                      fontSize={11}
                      fill={palette.primary}
                    />
                  ) : null}
                </Group>
              ) : null}
            </Group>
          );
        })}
    </>
  );
});

function buildRootPreview(body: string | null | undefined): string {
  const trimmed = body?.trim() ?? '';
  if (trimmed.length > 0) {
    const display = renderTokensToDisplay(trimmed, (_id, label) => label || undefined);
    return display.replace(/\s+/g, ' ');
  }
  return 'No comment text';
}

function truncatePreview(text: string): string {
  const limit = 96;
  if (text.length <= limit) {
    return text;
  }
  return `${text.slice(0, limit - 3)}...`;
}

function estimateLineCount(text: string): number {
  if (!text.length) {
    return 1;
  }
  const charsPerLine = 32;
  return Math.max(1, Math.ceil(text.length / charsPerLine));
}

function countReplies(replies: CommentNode[] | undefined): number {
  if (!replies || replies.length === 0) {
    return 0;
  }
  return replies.reduce((total, reply) => total + 1 + countReplies(reply.replies), 0);
}

interface TooltipPalette {
  surface: string;
  text: string;
  muted: string;
  primary: string;
  border: string;
  shadow: string;
}

function getThemePalette(): TooltipPalette {
  const root = typeof window !== 'undefined' ? document.documentElement : null;
  if (!root) {
    return {
      surface: '#FFFFFF',
      text: '#0F172A',
      muted: '#64748B',
      primary: '#3478DD',
      border: '#E5E7EB',
      shadow: 'rgba(15, 23, 42, 0.14)',
    };
  }
  const styles = getComputedStyle(root);
  const surface = styles.getPropertyValue('--background').trim() || '#FFFFFF';
  const text = styles.getPropertyValue('--foreground').trim() || '#0F172A';
  const muted = styles.getPropertyValue('--muted-foreground').trim() || '#64748B';
  const primary = styles.getPropertyValue('--primary').trim() || '#3478DD';
  const border = styles.getPropertyValue('--border').trim() || '#E5E7EB';
  const shadowBase = styles.getPropertyValue('--foreground').trim() || '#0F172A';
  const shadow = toShadowColor(shadowBase);
  return {
    surface,
    text,
    muted,
    primary,
    border,
    shadow,
  };
}

function toShadowColor(hex: string): string {
  const normalized = normaliseHex(hex);
  if (!normalized) {
    return 'rgba(15, 23, 42, 0.18)';
  }
  return hexToRgba(normalized, 0.14);
}

function animateNodeScale(node: Konva.Group | null, scale: number): void {
  if (!node) {
    return;
  }
  node.to({
    scaleX: scale,
    scaleY: scale,
    duration: 0.16,
    easing: Konva.Easings.EaseOut,
  });
}

interface TooltipOffsetInput {
  stage: Konva.Stage | null;
  location: { x: number; y: number };
  width: number;
  height: number;
  baseOffsetX: number;
  baseOffsetY: number;
  margin: number;
}

function computeTooltipOffsets({
  stage,
  location,
  width,
  height,
  baseOffsetX,
  baseOffsetY,
  margin,
}: TooltipOffsetInput): { offsetX: number; offsetY: number } {
  if (!stage) {
    return { offsetX: baseOffsetX, offsetY: baseOffsetY };
  }

  const container = stage.container();
  const parentRect = container.parentElement?.getBoundingClientRect() ?? container.getBoundingClientRect();
  const parentWidth = parentRect?.width ?? 0;
  const parentHeight = parentRect?.height ?? 0;
  if (!parentWidth || !parentHeight) {
    return { offsetX: baseOffsetX, offsetY: baseOffsetY };
  }

  const rawScaleX = stage.scaleX();
  const rawScaleY = stage.scaleY();
  const scaleX = rawScaleX === 0 ? 1 : rawScaleX;
  const scaleY = rawScaleY === 0 ? 1 : rawScaleY;
  const position = stage.position();

  let offsetX = baseOffsetX;
  let offsetY = baseOffsetY;

  const commentScreenX = position.x + location.x * scaleX;
  const commentScreenY = position.y + location.y * scaleY;

  let previewLeft = commentScreenX + offsetX * scaleX;
  let previewTop = commentScreenY + offsetY * scaleY;
  let previewRight = previewLeft + width * scaleX;
  let previewBottom = previewTop + height * scaleY;

  if (previewRight > parentWidth - margin) {
    const delta = previewRight - (parentWidth - margin);
    offsetX -= delta / scaleX;
    previewLeft -= delta;
    previewRight -= delta;
  }

  if (previewLeft < margin) {
    const delta = margin - previewLeft;
    offsetX += delta / scaleX;
    previewLeft += delta;
    previewRight += delta;
  }

  if (previewTop < margin) {
    const delta = margin - previewTop;
    offsetY += delta / scaleY;
    previewTop += delta;
    previewBottom += delta;
  }

  if (previewBottom > parentHeight - margin) {
    const delta = previewBottom - (parentHeight - margin);
    offsetY -= delta / scaleY;
    previewTop -= delta;
    previewBottom -= delta;
  }

  return { offsetX, offsetY };
}
